import PropTypes from 'prop-types';

import Select from '#globalComponents/Forms/Select';
import Textarea from '#globalComponents/Forms/Textarea';
import TextInput from '#globalComponents/Forms/TextInput';

const getInputType = (fieldType) => {
	if (fieldType === 'number') return 'number';
	if (fieldType === 'date') return 'date';
	return 'text';
};

/**
 * Renders the appropriate edit-mode input for a custom field based on its fieldType.
 *
 * @param {Object} props
 * @param {Object} props.def - Field definition (fieldType, selectOptions, etc.)
 * @param {*} props.value - Current draft value
 * @param {Function} props.onChange - Called with the new value (string or event-derived)
 * @param {Function} props.onBlur - Called when the input loses focus (triggers commit)
 * @param {Function} [props.onKeyDown] - Optional keydown handler (e.g. Enter/Escape)
 * @param {boolean} [props.error] - Marks the field as invalid
 */
const CustomFieldInput = ({ def, value, onChange, onBlur, onKeyDown, error = false }) => {
	if (def.fieldType === 'select') {
		return (
			<Select
				value={value ?? ''}
				onChange={(e) => onChange(e.target.value)}
				onBlur={onBlur}
				error={error}
				autoFocus
			>
				<option value="">–</option>
				{(def.selectOptions ?? []).map((opt) => (
					<option key={opt} value={opt}>
						{opt}
					</option>
				))}
			</Select>
		);
	}

	if (def.fieldType === 'textarea') {
		return (
			<Textarea
				value={value ?? ''}
				onChange={(e) => onChange(e.target.value)}
				onBlur={onBlur}
				rows={2}
				error={error}
				className="!resize-none"
				autoFocus
			/>
		);
	}

	return (
		<TextInput
			type={getInputType(def.fieldType)}
			value={value ?? ''}
			onChange={(e) => onChange(e.target.value)}
			onBlur={onBlur}
			onKeyDown={onKeyDown}
			error={error}
			autoFocus
		/>
	);
};

CustomFieldInput.propTypes = {
	def: PropTypes.object.isRequired,
	value: PropTypes.any,
	onChange: PropTypes.func.isRequired,
	onBlur: PropTypes.func.isRequired,
	onKeyDown: PropTypes.func,
	error: PropTypes.bool,
};

export default CustomFieldInput;
